import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Check, X, Phone, Mail, Car, ChevronLeft, ChevronRight, User, Trash2, RefreshCw, Plus, Search, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { testDriveApi, carsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function AdminTestDrive() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDaySlots, setSelectedDaySlots] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Manual booking modal state
  const [showManualBooking, setShowManualBooking] = useState(false);
  const [cars, setCars] = useState([]);
  const [carSearch, setCarSearch] = useState('');
  const [manualBooking, setManualBooking] = useState({
    car_id: '',
    date: '',
    time_slot: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    status: 'accepted',
    send_email: true
  });
  const [submittingManual, setSubmittingManual] = useState(false);

  // Load auto-generated calendar data for current month
  const loadCalendar = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await testDriveApi.getAutoCalendar(year, month);
      setCalendarData(response.data || response || []);
    } catch (err) {
      console.error('Error loading calendar:', err);
      toast.error('Fehler beim Laden des Kalenders');
    }
  }, [currentMonth]);

  // Load bookings
  const loadBookings = useCallback(async () => {
    try {
      const response = await testDriveApi.getBookings();
      setBookings(response.data || response || []);
    } catch (err) {
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load cars for manual booking
  const loadCars = useCallback(async () => {
    try {
      const response = await carsApi.getAll({ is_active: true, is_sold: false });
      setCars(response.data?.cars || response.data || []);
    } catch (err) {
      console.error('Error loading cars:', err);
    }
  }, []);

  // Load slots for selected date
  const loadSlotsForDate = useCallback(async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    try {
      const response = await testDriveApi.getAvailableSlots(date);
      setSelectedDaySlots(response.data || response);
    } catch (err) {
      console.error('Error loading slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    loadCalendar();
    loadBookings();
    loadCars();
  }, [loadCalendar, loadBookings, loadCars]);

  // Reload calendar when month changes
  useEffect(() => {
    loadCalendar();
  }, [currentMonth, loadCalendar]);

  // Load slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      loadSlotsForDate(selectedDate);
    } else {
      setSelectedDaySlots(null);
    }
  }, [selectedDate, loadSlotsForDate]);

  // Refresh data after booking action
  const refreshData = async () => {
    await Promise.all([loadCalendar(), loadBookings()]);
    if (selectedDate) {
      await loadSlotsForDate(selectedDate);
    }
  };

  // Get days in month for grid display
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const dayData = calendarData.find(d => d.date === dateStr);
      days.push({ 
        day: i, 
        date: dateStr,
        available: dayData?.available || false,
        totalSlots: dayData?.total_slots || 0,
        availableSlots: dayData?.available_slots || 0,
        bookedSlots: dayData?.booked_slots || []
      });
    }
    
    return days;
  };

  // Handle booking action
  const handleBookingAction = async (bookingId, action) => {
    if (action === 'reject' && !rejectModal) {
      setRejectModal(bookingId);
      return;
    }
    
    try {
      await testDriveApi.updateBookingStatus(bookingId, action, action === 'reject' ? rejectReason : null);
      toast.success(action === 'accept' ? 'Probefahrt bestätigt & E-Mail gesendet' : 'Probefahrt abgelehnt & E-Mail gesendet');
      setRejectModal(null);
      setRejectReason('');
      await refreshData();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Delete booking
  const handleDeleteBooking = async (bookingId) => {
    if (!confirm('Buchung wirklich löschen?')) return;
    try {
      await testDriveApi.deleteBooking(bookingId);
      toast.success('Buchung gelöscht');
      await refreshData();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Handle manual booking submit
  const handleManualBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualBooking.car_id || !manualBooking.date || !manualBooking.time_slot || !manualBooking.name || !manualBooking.email || !manualBooking.phone) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setSubmittingManual(true);
    try {
      await testDriveApi.adminBook(manualBooking);
      toast.success(manualBooking.send_email ? 'Buchung erstellt & E-Mail an Kunden gesendet' : 'Buchung erstellt');
      setShowManualBooking(false);
      setManualBooking({
        car_id: '',
        date: '',
        time_slot: '',
        name: '',
        email: '',
        phone: '',
        message: '',
        status: 'accepted',
        send_email: true
      });
      await refreshData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Fehler beim Erstellen der Buchung';
      toast.error(errorMsg);
    } finally {
      setSubmittingManual(false);
    }
  };

  // Navigate months
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  const days = getDaysInMonth(currentMonth);
  const today = new Date().toISOString().split('T')[0];

  // Filter bookings by status
  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const acceptedBookings = bookings.filter(b => b.status === 'accepted');
  const processedBookings = bookings.filter(b => b.status !== 'pending');

  // Filter cars for manual booking
  const filteredCars = cars.filter(car => {
    if (!carSearch) return true;
    const search = carSearch.toLowerCase();
    return (
      car.brand?.toLowerCase().includes(search) ||
      car.model?.toLowerCase().includes(search) ||
      `${car.brand} ${car.model}`.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
            <Calendar className="text-gold" size={28} />
            Probefahrt-Kalender
          </h1>
          <p className="text-muted-foreground mt-1">
            Verfügbarkeit wird automatisch aus den Modul-Einstellungen generiert
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/test-drive/archive"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm"
          >
            <Archive size={16} />
            Archiv
          </Link>
          <Button
            onClick={() => setShowManualBooking(true)}
            className="gap-2 bg-gold hover:bg-gold/90 text-black"
            data-testid="manual-booking-btn"
          >
            <Plus size={16} />
            Manuelle Buchung
          </Button>
          <Button
            onClick={refreshData}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <RefreshCw size={16} />
            Aktualisieren
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar Section */}
        <div className="bg-card border border-border rounded-lg p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 hover:bg-muted rounded-lg">
              <ChevronLeft size={20} />
            </button>
            <h2 className="font-heading font-medium text-lg">
              {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <button onClick={nextMonth} className="p-2 hover:bg-muted rounded-lg">
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((dayInfo, index) => {
              if (!dayInfo) {
                return <div key={`empty-${index}`} className="aspect-square"></div>;
              }
              
              const isPast = dayInfo.date < today;
              const isToday = dayInfo.date === today;
              const isSelected = dayInfo.date === selectedDate;
              const hasBookings = dayInfo.bookedSlots?.length > 0;
              const isFullyBooked = dayInfo.availableSlots === 0 && dayInfo.totalSlots > 0;
              
              return (
                <button
                  key={dayInfo.date}
                  onClick={() => !isPast && setSelectedDate(dayInfo.date)}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all relative
                    ${isPast ? 'opacity-40 cursor-not-allowed bg-muted' : 'cursor-pointer hover:scale-105'}
                    ${dayInfo.available && !isPast ? 'bg-green-500/20 border-2 border-green-500 text-green-700' : ''}
                    ${!dayInfo.available && !isPast ? 'bg-red-500/10 border border-red-300 text-red-600' : ''}
                    ${isFullyBooked && !isPast ? 'bg-orange-500/20 border-2 border-orange-500 text-orange-700' : ''}
                    ${isToday ? 'ring-2 ring-gold ring-offset-2' : ''}
                    ${isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}
                  `}
                  disabled={isPast}
                  data-testid={`calendar-day-${dayInfo.date}`}
                >
                  <span className="font-medium">{dayInfo.day}</span>
                  {dayInfo.totalSlots > 0 && !isPast && (
                    <span className={`text-[10px] ${isFullyBooked ? 'text-orange-600' : 'text-green-600'}`}>
                      {dayInfo.availableSlots}/{dayInfo.totalSlots}
                    </span>
                  )}
                  {hasBookings && !isPast && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-green-500/20 border border-green-500"></div>
              <span>Verfügbar</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-orange-500/20 border border-orange-500"></div>
              <span>Ausgebucht</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-red-500/10 border border-red-300"></div>
              <span>Geschlossen</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span>Hat Buchungen</span>
            </div>
          </div>

          {/* Selected Day Slots */}
          {selectedDate && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Clock size={16} />
                  Zeitfenster für {selectedDate}
                </h3>
              </div>
              
              {loadingSlots ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold"></div>
                </div>
              ) : selectedDaySlots?.available === false ? (
                <p className="text-sm text-muted-foreground">
                  An diesem Tag sind keine Probefahrten möglich. 
                  <br />
                  <span className="text-xs">Prüfen Sie die Öffnungszeiten in den Modul-Einstellungen.</span>
                </p>
              ) : selectedDaySlots?.slots?.length === 0 ? (
                <p className="text-sm text-orange-600">
                  Alle Zeitfenster sind bereits gebucht.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-2">
                    {selectedDaySlots?.slots?.length || 0} von {selectedDaySlots?.total_slots || 0} Slots verfügbar
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedDaySlots?.slots?.map((slot, index) => (
                      <div key={index} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                        <Check size={14} className="text-green-600" />
                        <span>{slot.start} - {slot.end}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show booked slots for this day */}
              {selectedDate && bookings.filter(b => b.date === selectedDate && b.status !== 'rejected').length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h4 className="text-sm font-medium mb-2 text-red-600">Gebuchte Slots:</h4>
                  <div className="space-y-2">
                    {bookings
                      .filter(b => b.date === selectedDate && b.status !== 'rejected')
                      .map(booking => (
                        <div key={booking.id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                          <div>
                            <span className="font-medium">{booking.time_slot}</span>
                            <span className="text-muted-foreground ml-2">
                              - {booking.name} ({booking.car_info?.brand} {booking.car_info?.model})
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            booking.status === 'accepted' ? 'bg-green-500/20 text-green-700' : 'bg-amber-500/20 text-amber-700'
                          }`}>
                            {booking.status === 'accepted' ? 'Bestätigt' : 'Ausstehend'}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bookings Section */}
        <div className="space-y-4">
          {/* Pending Bookings */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="font-heading font-medium text-lg mb-4 flex items-center gap-2">
              <Clock className="text-amber-500" size={20} />
              Neue Anfragen ({pendingBookings.length})
            </h2>
            
            {pendingBookings.length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine ausstehenden Anfragen</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {pendingBookings.map(booking => (
                  <div key={booking.id} className="bg-muted/50 rounded-lg p-3 border border-border" data-testid={`booking-${booking.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <User size={14} />
                          {booking.name}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Car size={12} />
                          {booking.car_info?.brand} {booking.car_info?.model} ({booking.car_info?.year})
                        </p>
                      </div>
                      <span className="text-xs bg-amber-500/20 text-amber-700 px-2 py-1 rounded">
                        Ausstehend
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar size={12} />
                        {booking.date}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock size={12} />
                        {booking.time_slot}
                      </div>
                      <a href={`tel:${booking.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                        <Phone size={12} />
                        {booking.phone}
                      </a>
                      <a href={`mailto:${booking.email}`} className="flex items-center gap-1 text-primary hover:underline truncate">
                        <Mail size={12} />
                        {booking.email}
                      </a>
                    </div>
                    
                    {booking.message && (
                      <p className="text-sm text-muted-foreground bg-background p-2 rounded mb-3 italic">
                        "{booking.message}"
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleBookingAction(booking.id, 'accept')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1"
                        data-testid={`accept-${booking.id}`}
                      >
                        <Check size={14} /> Annehmen
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBookingAction(booking.id, 'reject')}
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 gap-1"
                        data-testid={`reject-${booking.id}`}
                      >
                        <X size={14} /> Ablehnen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirmed Bookings */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="font-heading font-medium text-lg mb-4 flex items-center gap-2">
              <Check className="text-green-500" size={20} />
              Bestätigte Probefahrten ({acceptedBookings.length})
            </h2>
            
            {acceptedBookings.length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine bestätigten Probefahrten</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {acceptedBookings.map(booking => (
                  <div key={booking.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">{booking.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {booking.car_info?.brand} {booking.car_info?.model}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{booking.date}</p>
                      <p className="text-xs text-green-600">{booking.time_slot}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <a 
                        href={`tel:${booking.phone}`} 
                        className="p-1.5 hover:bg-green-100 rounded"
                        title={booking.phone}
                      >
                        <Phone size={14} className="text-green-600" />
                      </a>
                      <button
                        onClick={() => handleDeleteBooking(booking.id)}
                        className="p-1.5 hover:bg-red-100 rounded text-red-500"
                        title="Löschen"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Processed Bookings (Rejected) */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="font-heading font-medium text-lg mb-4">
              Abgelehnte Anfragen ({processedBookings.filter(b => b.status === 'rejected').length})
            </h2>
            
            {processedBookings.filter(b => b.status === 'rejected').length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine abgelehnten Anfragen</p>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {processedBookings.filter(b => b.status === 'rejected').map(booking => (
                  <div key={booking.id} className="flex items-center justify-between bg-red-50/50 border border-red-100 rounded-lg p-2 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <div>
                        <p className="font-medium">{booking.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {booking.date} • {booking.car_info?.brand} {booking.car_info?.model}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteBooking(booking.id)}
                      className="p-1 text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 max-w-md w-full">
            <h3 className="font-heading font-medium text-lg mb-4">Probefahrt ablehnen</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Optional: Geben Sie einen Grund für die Ablehnung an (wird per E-Mail an den Kunden gesendet)
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="z.B. Das Fahrzeug ist zu diesem Zeitpunkt nicht verfügbar..."
              className="w-full border border-border rounded-lg p-3 text-sm min-h-[100px] mb-4"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setRejectModal(null); setRejectReason(''); }}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => handleBookingAction(rejectModal, 'reject')}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Ablehnen & E-Mail senden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Booking Modal */}
      {showManualBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card rounded-lg p-6 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-medium text-xl flex items-center gap-2">
                <Plus className="text-gold" size={24} />
                Manuelle Probefahrt-Buchung
              </h3>
              <button
                onClick={() => setShowManualBooking(false)}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Erstellen Sie eine Buchung für telefonische Anfragen. Der Kunde erhält automatisch eine E-Mail.
            </p>
            
            <form onSubmit={handleManualBookingSubmit} className="space-y-4">
              {/* Car Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Fahrzeug *</label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <input
                    type="text"
                    value={carSearch}
                    onChange={(e) => setCarSearch(e.target.value)}
                    placeholder="Fahrzeug suchen..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-border rounded-lg">
                  {filteredCars.map(car => (
                    <button
                      key={car.id}
                      type="button"
                      onClick={() => setManualBooking(prev => ({ ...prev, car_id: car.id }))}
                      className={`w-full flex items-center gap-3 p-2 text-left hover:bg-muted/50 ${
                        manualBooking.car_id === car.id ? 'bg-primary/10 border-l-2 border-primary' : ''
                      }`}
                    >
                      <Car size={16} className="text-muted-foreground" />
                      <span className="text-sm">{car.brand} {car.model} ({car.year})</span>
                      {manualBooking.car_id === car.id && <Check size={14} className="ml-auto text-primary" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Datum *</label>
                  <input
                    type="date"
                    value={manualBooking.date}
                    onChange={(e) => setManualBooking(prev => ({ ...prev, date: e.target.value }))}
                    min={today}
                    required
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Zeitfenster *</label>
                  <select
                    value={manualBooking.time_slot}
                    onChange={(e) => setManualBooking(prev => ({ ...prev, time_slot: e.target.value }))}
                    required
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Wählen...</option>
                    <option value="09:00-10:00">09:00 - 10:00</option>
                    <option value="10:00-11:00">10:00 - 11:00</option>
                    <option value="11:00-12:00">11:00 - 12:00</option>
                    <option value="12:00-13:00">12:00 - 13:00</option>
                    <option value="13:00-14:00">13:00 - 14:00</option>
                    <option value="14:00-15:00">14:00 - 15:00</option>
                    <option value="15:00-16:00">15:00 - 16:00</option>
                    <option value="16:00-17:00">16:00 - 17:00</option>
                    <option value="17:00-18:00">17:00 - 18:00</option>
                  </select>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    value={manualBooking.name}
                    onChange={(e) => setManualBooking(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="Max Mustermann"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">E-Mail *</label>
                  <input
                    type="email"
                    value={manualBooking.email}
                    onChange={(e) => setManualBooking(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder="max@beispiel.de"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Telefon *</label>
                  <input
                    type="tel"
                    value={manualBooking.phone}
                    onChange={(e) => setManualBooking(prev => ({ ...prev, phone: e.target.value }))}
                    required
                    placeholder="+43 123 456 789"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-2">Notiz (optional)</label>
                <textarea
                  value={manualBooking.message}
                  onChange={(e) => setManualBooking(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Telefonische Buchung..."
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm min-h-[60px]"
                />
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-4 p-3 bg-muted/50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={manualBooking.status === 'accepted'}
                    onChange={() => setManualBooking(prev => ({ ...prev, status: 'accepted' }))}
                    className="text-primary"
                  />
                  <span className="text-sm">Direkt bestätigen</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={manualBooking.status === 'pending'}
                    onChange={() => setManualBooking(prev => ({ ...prev, status: 'pending' }))}
                    className="text-primary"
                  />
                  <span className="text-sm">Als ausstehend anlegen</span>
                </label>
                <div className="w-px bg-border"></div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={manualBooking.send_email}
                    onChange={(e) => setManualBooking(prev => ({ ...prev, send_email: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">E-Mail an Kunden senden</span>
                </label>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualBooking(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={submittingManual}
                  className="flex-1 bg-gold hover:bg-gold/90 text-black"
                >
                  {submittingManual ? 'Wird erstellt...' : 'Buchung erstellen'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
